import React, { ClassAttributes, JSXElementConstructor, ReactElement, ReactPortal, TdHTMLAttributes, useImperativeHandle } from 'react';
import { FC, forwardRef, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
// React-table v7 documentation:
// https://github.com/TanStack/table/blob/v7/docs/src/pages/docs/quick-start.md
import { useTable, usePagination, useFilters, useRowSelect, useExpanded, useSortBy, UseTableRowProps } from 'react-table';
import TableCheckbox from './TableCheckbox';
import { IFilterButtonData, TableFilters } from './TableFilters';
import { REPORTSTATUS } from '../util/DataModelInterface';

interface IDataTableProps {
  tableheadings: string[],
  tabledata: any[],
  tableType: string,
  tableId: string,
  caption?: string,
  filters?: IFilterButtonData[],
  hiddenColumns?: string[],
  showFilters?: boolean,
  toolbar?: JSX.Element,
  selectHeader?: string,
  filterSet?: string,
  ref?: any,
  optSortableColumns?: string[],
  setTableMaxCount?: (count: number) => void,
  setSelected?: (items: any[]) => void,
  setSelectedButton?: (id: number) => void
}

const STATUSES = { 
  [REPORTSTATUS.NEW]: 0, 
  [REPORTSTATUS.ONGOING]: 1, 
  [REPORTSTATUS.CONFIRMED]: 2,
  [REPORTSTATUS.CLOSED]: 3, 
  [REPORTSTATUS.CORRECTED]: 4, 
  [REPORTSTATUS.BILLED]: 5,
  [REPORTSTATUS.REBILLED]: 6, 
  [REPORTSTATUS.REOPENED]: 7 };

export const DataTable: FC<IDataTableProps> = forwardRef((props, ref: any) => {
  const intl = useIntl();
  const lang = intl.locale;
  const [columns, setColumns] = useState([] as any[]);

  const stateSort = React.useMemo(() => {
    return (rowA: { original: any; }, rowB: { original: any }) => {
      let aVal;
      let bVal;
      if (rowA.original.status) { // State is a React Element, check status column instead!
        const aStatus = rowA.original.status;
        const bStatus = rowB.original.status;
        aVal = STATUSES[aStatus];
        bVal = STATUSES[bStatus];
      } else {
        aVal = rowA.original.state.toLowerCase();
        bVal = rowB.original.state.toLowerCase();
      }
      if (aVal > bVal) return 1;
      if (aVal < bVal) return -1;
      return 0;
    }
  }, [])

  const lowerCaseSort = React.useMemo(() => {
    return (rowA: { values: any}, rowB: { values: any; }, id: string | number) => {
      const a = rowA.values[id].toLowerCase();
      const b = rowB.values[id].toLowerCase();
      return a.localeCompare(b)
    }
  }, [])

  const periodSort = React.useMemo(() => {
    return (rowA: { values: any}, rowB: { values: any; }, id: string | number) => {
      if (rowA.values.period) {
        let periodA = rowA.values.period?.split("/");
        let periodB = rowB.values.period?.split("/");
        let aVal = new Date(periodA[1] + "-" + periodA[0]).getTime();
        let bVal = new Date(periodB[1] + "-" + periodB[0]).getTime();
        if (aVal > bVal) return 1;
        if (aVal < bVal) return -1;
        return 0;
      }
    }
  }, [])

  const periodViewSort = React.useMemo(() => {
    return (rowA: { original: any; }, rowB: { original: any }) => {
      let aVal;
      let bVal;
      let periodA;
      let periodB;
      if (rowA.original.period) { // periodView is a React Element, check period column instead!
        periodA = rowA.original.period?.split("/");
        periodB = rowB.original.period?.split("/");

      } else {
        periodA = rowA.original.periodView?.split("/");
        periodB = rowB.original.periodView?.split("/");
      }
      if(periodA && periodB) {
        aVal = new Date(periodA[1] + "-" + periodA[0]).getTime();
        bVal = new Date(periodB[1] + "-" + periodB[0]).getTime();
        if (aVal > bVal) return 1;
        if (aVal < bVal) return -1;
      }
      return 0;
    }
  }, [])


  const initFilters = () => {
    let filterArray = [];
    let filterInd = 0;
    if (props.filterSet && props.filters) {
      filterInd = props.filters.findIndex(f => f.button === props.filterSet) | 0;
    }
    // Set initial filter: first button filtering is set by default (id = column, value = applied filter values for the column)
    if (props.filters && props.filters[filterInd] && props.filters[filterInd].filters.length > 0) {
      props.filters[filterInd].filters.forEach((f) => {
        if (f.filterValues.length > 0) {
          let filter = {
            id: f.column,
            value: f.filterValues.length > 0 ? f.filterValues : ['']
          }
          filterArray.push(filter);
        }
      })
    } else {
        filterArray.push({id:'', value: ['']}); // If there are no filters, leave them empty
    }
    return filterArray;
  }

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    selectedFlatRows,
    toggleAllRowsSelected,
    toggleAllRowsExpanded,
    page,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setFilter,
    state: { pageIndex, pageSize },
    
  } = useTable({
    columns,
    data: props.tabledata,
    isMultiSortEvent: () => true,
    initialState:
      {
        pageIndex: 0,
        pageSize: 100,
        hiddenColumns: props.hiddenColumns ? props.hiddenColumns : [],
        filters: initFilters(),
      },
  }, 
  useFilters,
  useSortBy, // https://github.com/TanStack/table/blob/v7/docs/src/pages/docs/api/useSortBy.md
  useExpanded,
  usePagination,
  useRowSelect,
  (hooks) => {
    if(props.selectHeader)
      hooks.visibleColumns.push((columns) => [
        {
          id: "selection",
          Header: props.selectHeader,
          Cell: ({ row }) => {
            return <TableCheckbox {...row.getToggleRowSelectedProps()} />;
          },
        },
        ...columns,
      ]);
  });

  useEffect(() => {
    toggleAllRowsExpanded(true);
    toggleAllRowsSelected(props.selectHeader ? false : true); 
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.tabledata]);

  useEffect(() => {
    toggleAllRowsExpanded(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.filterSet]);

  useEffect(() => {
    const headers: any[] = [];
    for(let i = 0; i < props.tableheadings.length; i++) {
      let id = props.tableheadings[i];
      let sortType = 'alphanumeric' as any; // String options: basic, datetime, alphanumeric. Defaults to alphanumeric.
      if (id === 'state') sortType = stateSort;
      if (id === 'name') sortType = lowerCaseSort;
      if (id === 'company') sortType = lowerCaseSort;
      if (id === 'period') sortType = periodSort;
      if (id === 'periodView') sortType = periodViewSort;
      // eslint-disable-next-line eqeqeq
      headers.push({ id: props.tableheadings[i], Header: intl.formatMessage({id: props.tableType + '.' + props.tableheadings[i]}), accessor: props.tableheadings[i], sortType: sortType});
    }
    
    if(props.filters) {
      props.filters.map(fb => fb.filters).forEach(f => f.forEach(fd => {
        if(!headers.map((h) => h.id).includes(fd.column)) 
          headers.push({ id: fd.column, Header: fd.column, accessor: fd.column, filter: 'includesSome' });
      }));
    }
    setColumns( headers );
    // toggleAllRowsExpanded(true);
    toggleAllRowsSelected(props.selectHeader ? false : true); 
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.filters, props.selectHeader, lang]);

  useEffect(() => {
    if(props.setSelected)
      props.setSelected(selectedFlatRows.map(
        d => d.original
      ));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFlatRows]);

  useEffect(() => {
    if(props.setTableMaxCount)
      props.setTableMaxCount(rows.length);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  useImperativeHandle(ref, () => ({
    toggleSelectedRows: (set: boolean) => toggleAllRowsSelected(set),
  }), [toggleAllRowsSelected]);

  const getSortableColumn = (column : any) => {
    if (props.optSortableColumns?.includes(column.id)) {
      return <th {...column.getHeaderProps(column.getSortByToggleProps())} className={column.isSorted ? column.isSortedDesc ? "sorting_desc" : "sorting_asc" : "sorting"}>
                {column.render('Header')}
             </th>
    } else {
      return <th {...column.getHeaderProps()}>{column.render('Header')}</th>
    }
  }

  const rowCount = () => {
    let count = 0 ;
    rows.forEach((r) => {
      let original = r.original as UseTableRowProps<typeof r>;
      if(original.subRows) {
         count+=original?.subRows?.length;
      }
    });
    if(count===0) count = rows.length;
    return count;
  }

  const pagination = () => {
    return <div className="pagination">
      <span className="info" role="status" aria-live="polite">
        <FormattedMessage id={"reports.count." + props.tableType} 
          values={{start: pageIndex*pageSize+1, end: pageIndex*pageSize+pageSize > rowCount() ? rowCount() : pageIndex*pageSize+pageSize, total: rowCount()}}/>
      </span>
      <label>
        <select className="form-control"
          value={pageSize}
          onChange={e => { setPageSize(Number(e.target.value))}}>
          {[10, 25, 50, 100].map(pageSize => (
            <option key={pageSize} value={pageSize}>
              {pageSize}
            </option>
          ))}
        </select>
        <span><FormattedMessage id="reports.countonpage"/></span>
      </label>
      <div className='paging_buttons'>
        <button className="paging_button" key='first' onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
          <i className="fa fa-angle-double-left"/><span className="sr-only"><FormattedMessage id="reports.firstpage" /></span>
        </button>
        <button className="paging_button previous" key='prev' onClick={() => previousPage()} disabled={!canPreviousPage}>
          <i className="fa fa-chevron-left"/><span className="sr-only"><FormattedMessage id="reports.previouspage" /></span>
        </button>
        <button className="paging_button next" key='next' onClick={() => nextPage()} disabled={!canNextPage}>
          <i className="fa fa-chevron-right"/><span className="sr-only"><FormattedMessage id="reports.nextpage" /></span>
        </button>
        <button className="paging_button" key='last' onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
          <i className="fa fa-angle-double-right"/><span className="sr-only"><FormattedMessage id="reports.lastpage" /></span>
        </button>
      </div>
    </div>;
  }

  return (
    <>
      {props.showFilters && 
      <TableFilters tableId={props.tableId} tableType={props.tableType} 
                    filterdata={props.filters ? props.filters : [] as IFilterButtonData[]} 
                    setFilter={ (column: string, values: string[]) : void => { 
                        setFilter(column, values);
                      } }
                    setSelectedButton={ ( (id: number) => props.setSelectedButton ? props.setSelectedButton(id) : undefined ) }
                    />}
      <div className="table-responsive ">
        {pagination()}
        { props.toolbar ? props.toolbar : <></> }
        <table {...getTableProps()} className="table table-striped table-hover border-bottom mt-5" id={props.tableId}>
          <caption className="sr-only">{props.caption}</caption>
            <thead>
              {headerGroups.map(headerGroup => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map(column => getSortableColumn(column))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map((row: any, _i: any) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()} className={row.original.subRows && row.original.subRows.length > 0 ? 'subheader' : ''}>
                    {row.cells.map((cell: { getCellProps: () => JSX.IntrinsicAttributes & ClassAttributes<HTMLTableCellElement> & TdHTMLAttributes<HTMLTableCellElement>; render: (arg0: string) => string | number | boolean | ReactElement<any, string | JSXElementConstructor<any>> | ReactPortal | null | undefined; }) => {
                      return <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                    })}
                  </tr>
                )
              })}
            </tbody>
        </table>
        {pagination()}
      </div>
    </>
  );
});